import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, PlayCircle, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TIDS } from "@/lib/testIds";

const HERO_IMG =
  "https://images.pexels.com/photos/19879741/pexels-photo-19879741.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Masked line reveal
const lineWrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const line = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

function Line({ children, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span variants={line} className={`block ${className}`}>
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const scrollTo = (id) =>
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="top"
      ref={ref}
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="absolute inset-0 radial-glow pointer-events-none" />
      <div className="absolute inset-0 accent-glow pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Left: Copy */}
        <motion.div
          variants={lineWrap}
          initial="hidden"
          animate="show"
          className="lg:col-span-7"
        >
          <motion.div
            variants={fadeUp}
            className="inline-flex items-center gap-2 rounded-full bg-white/5 backdrop-blur-md border border-white/10 px-3 py-1.5 mb-8"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="font-mono-accent text-[11px] uppercase tracking-[0.2em] text-white/80">
              Now booking Q1 partnerships
            </span>
          </motion.div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tightest leading-[1.02] text-white">
            <Line>The undeniable</Line>
            <Line className="italic font-light text-white/70">marketing system</Line>
            <Line>
              for modern{" "}
              <span className="relative inline-block">
                <span className="text-amber-400">dentists</span>
                <svg
                  className="absolute -bottom-2 left-0 w-full"
                  viewBox="0 0 200 12"
                  preserveAspectRatio="none"
                >
                  <motion.path
                    d="M2 8 Q 100 -2 198 8"
                    stroke="#F59E0B"
                    strokeWidth="2.5"
                    fill="none"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.9, delay: 1.1, ease: "easeInOut" }}
                  />
                </svg>
              </span>
              .
            </Line>
          </h1>

          <motion.p
            variants={fadeUp}
            className="mt-8 text-lg md:text-xl text-white/70 max-w-xl leading-relaxed"
          >
            We build predictable, high-converting patient acquisition machines so
            you can stop chasing leads and start filling your chair, every single
            week.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-10 flex flex-col sm:flex-row gap-4">
            <Button
              data-testid={TIDS.heroBookCall}
              onClick={() => scrollTo("#booking")}
              className="group h-12 px-6 rounded-full bg-amber-400 text-[#070B14] hover:bg-amber-300 font-medium text-base transition-colors duration-200 active:scale-95"
            >
              Book a free strategy call
              <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
            </Button>
            <Button
              data-testid={TIDS.heroSeeResults}
              onClick={() => scrollTo("#results")}
              variant="ghost"
              className="h-12 px-6 rounded-full text-white border border-white/15 hover:bg-white/5 hover:text-white font-medium text-base"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              See the numbers
            </Button>
          </motion.div>

          <motion.div variants={fadeUp} className="mt-12 flex items-center gap-6">
            <div className="flex -space-x-3">
              {[
                "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=100&auto=format&fit=crop&q=80",
                "https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=100&auto=format&fit=crop&q=80",
                "https://images.unsplash.com/photo-1537368910025-700350fe46c7?w=100&auto=format&fit=crop&q=80",
                "https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=100&auto=format&fit=crop&q=80",
              ].map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  className="w-10 h-10 rounded-full border-2 border-[#070B14] object-cover"
                />
              ))}
            </div>
            <div>
              <div className="flex items-center gap-1 text-amber-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-amber-400" />
                ))}
              </div>
              <div className="text-xs text-white/60 mt-1 font-mono-accent uppercase tracking-widest">
                Trusted by 140+ practices
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Right: Image with spotlight frame + parallax */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
          >
            <motion.img
              data-testid={TIDS.heroImage}
              src={HERO_IMG}
              alt="Modern dentist treating patient"
              style={{ y: imgY, scale: imgScale }}
              className="w-full h-[440px] md:h-[560px] object-cover"
              loading="eager"
            />
            {/* spotlight */}
            <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_65%_35%,rgba(255,255,255,0.12),transparent_70%)]" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#070B14]/70 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            style={{ y: badgeY }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="hidden md:block absolute -left-8 top-16 bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl p-4 shadow-[0_8px_32px_rgba(0,0,0,0.4)] w-56"
          >
            <div className="text-[10px] font-mono-accent uppercase tracking-[0.2em] text-blue-300/80">
              This month
            </div>
            <div className="mt-2 font-display text-3xl font-semibold text-white">+327</div>
            <div className="text-xs text-white/70">new patient bookings across our clients</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="hidden md:block absolute -right-6 bottom-10 bg-amber-400 text-[#070B14] rounded-2xl p-4 shadow-[0_20px_60px_rgba(245,158,11,0.35)] w-52 rotate-3"
          >
            <div className="text-[10px] font-mono-accent uppercase tracking-[0.2em]">Avg. ROAS</div>
            <div className="mt-1 font-display text-3xl font-semibold">6.4×</div>
            <div className="text-xs">Return on ad spend, verified</div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
